/**
 * A new URL-improve article stays enhanced "pending" until the user clicks
 * Enhance article / Generovat SEO článek. This gate does not enqueue
 * OpenAIArticleGenerate. ?cesta=immediate and a paid allowAutoStart used to
 * start parts as soon as scraped headings were saved. Status null and
 * enhanced pending are not a running write: the parts deadline must not
 * flip them to failed.
 * It does not write Firestore and does not touch article HTML.
 */

export type UrlImproveGate = {
    analysisLoading: boolean;
    briefLoading: boolean;
    customizeFirst: boolean;
};

export type UrlImproveEnqueueInput = {
    isAnalyze: boolean;
    enhanced?: string | null;
    aiGenerateStatus?: string | null;
    aiGenerateMode?: string | null;
    customizeFirst: boolean;
    analysisLoading: boolean;
    briefLoading: boolean;
    scrapeLoading: boolean;
    scrapedHeadingsSaved: boolean;
    allowAutoStart: boolean;
    /** Article route ?cesta=immediate. Must not enqueue parts generation. */
    immediateRoute?: boolean;
    now: number;
    waitingSince?: number | null;
};

export type UrlImproveEnqueueDecision =
    | {action: 'none'}
    | {action: 'wait'; waitMs: number}
    | {action: 'enqueue'}
    | {action: 'failed'};

const DEFAULT_GATE: UrlImproveGate = {
    analysisLoading: true,
    briefLoading: true,
    customizeFirst: false,
};

let gate: UrlImproveGate = {...DEFAULT_GATE};
const listeners = new Set<() => void>();

export function readUrlImproveGate(): UrlImproveGate {
    return gate;
}

export function setUrlImproveGate(next: UrlImproveGate): void {
    gate = {
        analysisLoading: !!next.analysisLoading,
        briefLoading: !!next.briefLoading,
        customizeFirst: !!next.customizeFirst,
    };
    listeners.forEach((listener) => listener());
}

export function resetUrlImproveGate(): void {
    setUrlImproveGate(DEFAULT_GATE);
}

export function onUrlImproveGate(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Paid allowAutoStart + not customize-first + headings already saved used to
 * enqueue with no click. cesta=immediate is that path (customizeFirst stays
 * false). Both stay blocked: writing starts only on the article-page button.
 */
export function urlImproveAutoEnqueueBlocked(input: Pick<
    UrlImproveEnqueueInput,
    'allowAutoStart' | 'customizeFirst' | 'scrapedHeadingsSaved' | 'immediateRoute'
>): boolean {
    if (input.immediateRoute) {
        return true;
    }
    if (input.allowAutoStart && !input.customizeFirst && input.scrapedHeadingsSaved) {
        return true;
    }
    return false;
}

/**
 * Parts generate (isAnalyze), never whole-article mode.
 * This decision never enqueues. "Vylepšit hned" / ?cesta=immediate only
 * prepares the article. decideUrlImproveEnqueue used to return enqueue for a
 * paid account as soon as scraped headings existed, and applyUrlImproveEnqueue
 * then called manualEnhance with no click.
 * Writing starts only when the user clicks Enhance article / Generovat SEO
 * článek (manualEnhance). Status null is not a stuck run, even when
 * created_at is older than the parts limit.
 */
export function decideUrlImproveEnqueue(input: UrlImproveEnqueueInput): UrlImproveEnqueueDecision {
    if (!input.isAnalyze || input.enhanced !== 'pending') {
        return {action: 'none'};
    }
    if (input.aiGenerateMode === 'whole') {
        return {action: 'none'};
    }
    if (input.aiGenerateStatus === 'failed'
        || input.aiGenerateStatus === 'done'
        || input.aiGenerateStatus === 'running') {
        return {action: 'none'};
    }

    // Status null. Read the old auto-start inputs so this path still cannot
    // enqueue, and do not turn the open article into failed.
    urlImproveAutoEnqueueBlocked(input);
    return {action: 'none'};
}
